import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Header } from "../header";
import { AppShell } from "@/components/Navbar";
import clsx from "clsx";
import { Button } from "@/components/ui/button";
import { ValuesSection } from "../components/ValuesSection";
import OurStoryOfGrowth from "./sections/OurStoryOfGrowth";
import { useAboutInfo } from "@/api/information";
import MeetOurTeam from "./sections/MeetOurTeam";
import { Skeleton } from "@/components/ui/skeleton";
import YourRTK from "./sections/YourRTK";

export const AboutUs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useAboutInfo({ lang: i18n.language });
  const aboutData = data?.about;
  const headerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setIsScrolled(rect.bottom <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (isError) {
    return <div>Error loading about us information.</div>;
  }
  return (
    <>
      <Helmet>
        <title>{t("aboutPage.metaTitle")}</title>
        <meta name="description" content={t("aboutPage.metaDescription")} />
        <meta name="keywords" content={t("aboutPage.metaKeywords")} />
        <link rel="canonical" href="https://efb.eg/about-us" />
        <meta property="og:title" content={t("aboutPage.ogTitle")} />
        <meta property="og:description" content={t("aboutPage.ogDescription")} />
        <meta property="og:url" content="https://efb.eg/about-us" />
        <meta property="og:site_name" content={t("siteName")} />
      </Helmet>
      <div ref={headerRef}>
        <Header
          title={aboutData?.title}
          scrollHref="#values"
          picture={aboutData?.cover_image?.large_url}
          light
          aboutUs
        />
      </div>
      <AppShell
        navbarClass={clsx(
          "fixed top-0 -z-10 w-full bg-[#CDCFC9] opacity-0 transition-opacity duration-300 ease-in-out",
          { "opacity-100 z-50": isScrolled },
        )}
      />
      <section className="mx-4 my-8 md:mx-auto md:my-24 md:px-20 lg:px-80">
        {isLoading ? (
          <Skeleton className="h-56 w-full bg-[#E2DDD7]" />
        ) : (
          <p className="text-2xl">{aboutData?.description}</p>
        )}
      </section>
      <YourRTK yourRightToKnow={aboutData?.your_right_to_know} isLoading={isLoading} />

      <section id="values" className="my-8 p-6 md:my-16">
        <h5 className="text-2xl font-semibold md:text-5xl">{t("aboutPage.values")}</h5>
        <div className="my-6 grid-cols-3 gap-6 md:grid">
          {isLoading || !aboutData ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                className="mt-6 flex h-[200px] rounded-[16px] bg-[#E2DDD7] p-4 md:mt-0 md:h-[290px] lg:h-[264px]"
              />
            ))
          ) : (
            <ValuesSection valuesData={aboutData.our_values} />
          )}
        </div>
      </section>

      <section className="my-8 p-6 md:my-16">
        <div className="flex w-full items-center justify-between">
          <h4 className="text-2xl font-semibold md:text-5xl">{t("aboutPage.ourTeam")}</h4>
          <Button variant="default" size="lg" className="hidden md:block">
            {t("aboutPage.joinOurTeam")}
          </Button>
        </div>

        <MeetOurTeam meetOurTeam={aboutData?.meet_our_team} isLoading={isLoading} />
        <Button variant="default" size="lg" className="mt-6 block w-full md:hidden">
          {t("aboutPage.joinOurTeam")}
        </Button>
      </section>

      <section className="mx-6 my-8 flex flex-col items-start gap-y-8 rounded-2xl bg-[#E2DDD7] p-6 md:my-16">
        <h4 className="text-2xl font-semibold md:text-5xl">{t("aboutPage.ourStoryOfGrowth")}</h4>
        <OurStoryOfGrowth ourStoryOfGrowth={aboutData?.our_story_of_growth} isLoading={isLoading} />
      </section>
    </>
  );
};
